import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Users, Pencil, Trash2, Eye, EyeOff, ExternalLink, Loader2, Share2 } from "lucide-react";

export default function WebinarsList() {
  const [webinars, setWebinars] = useState(null);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get("/admin/webinars").then((r) => setWebinars(r.data)).catch(() => setWebinars([]));
  useEffect(() => { load(); }, []);

  const createWebinar = async () => {
    setCreating(true);
    try {
      const res = await api.post("/admin/webinars", {
        title: "Untitled Experience",
        description: "",
        duration_seconds: 1800,
        hosts: [],
      });
      navigate(`/admin/webinars/${res.data.id}`);
    } catch {
      toast.error("Failed to create webinar");
      setCreating(false);
    }
  };

  const remove = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm("Delete this webinar and all its registrants?")) return;
    await api.delete(`/admin/webinars/${id}`);
    toast.success("Webinar deleted");
    load();
  };

  return (
    <div className="p-8" data-testid="webinars-list-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-black tracking-tighter">Webinars</h1>
          <p className="text-[#4B5563] mt-1">Create and manage your sessions.</p>
        </div>
        <button
          onClick={createWebinar}
          disabled={creating}
          data-testid="create-webinar-btn"
          className="bg-[#6D28B5] text-white hover:bg-[#4A1A7A] disabled:opacity-50 px-5 py-2.5 font-semibold transition-colors flex items-center gap-2"
        >
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} New webinar
        </button>
      </div>

      {webinars === null ? (
        <div className="text-[#9CA3AF]">Loading…</div>
      ) : webinars.length === 0 ? (
        <div className="border border-dashed border-[#9CA3AF] bg-white p-16 text-center" data-testid="webinars-empty">
          <p className="text-[#4B5563] font-medium mb-4">No webinars yet.</p>
          <button onClick={createWebinar} className="bg-[#6D28B5] text-white px-5 py-2.5 font-semibold inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> Create your first webinar
          </button>
        </div>
      ) : (
        <div className="bg-white border border-[#0A0A0A]">
          {webinars.map((w, i) => (
            <Link
              to={`/admin/webinars/${w.id}`}
              key={w.id}
              data-testid={`webinar-row-${w.id}`}
              className={`flex items-center gap-4 p-4 hover:bg-[#F3F4F6] transition-colors ${i > 0 ? "border-t border-[#E5E7EB]" : ""}`}
            >
              <div className="w-16 h-12 bg-[#E5E7EB] overflow-hidden shrink-0 border border-[#E5E7EB]">
                {w.image_url && <img src={w.image_url} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{w.title}</div>
                <div className="text-xs text-[#4B5563] flex items-center gap-2 mt-0.5">
                  <span className="font-mono">/{w.slug}</span>
                  <span>·</span>
                  <span>{w.price > 0 ? `$${w.price}` : "Free"}</span>
                  <span>·</span>
                  <span className="capitalize">{w.mode}</span>
                </div>
              </div>
              <span className={`text-xs font-bold uppercase tracking-wide px-2 py-1 flex items-center gap-1 ${w.published ? "text-[#10B981]" : "text-[#9CA3AF]"}`}>
                {w.published ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                {w.published ? "Live" : "Draft"}
              </span>
              <div className="flex items-center gap-1 shrink-0">
                {w.published && (
                  <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(`/w/${w.slug}`, "_blank"); }} className="p-2 hover:bg-[#E5E7EB]" title="Preview" data-testid={`preview-${w.id}`}>
                    <ExternalLink className="w-4 h-4 text-[#4B5563]" />
                  </button>
                )}
                {w.published && (
                  <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigator.clipboard.writeText(`${window.location.origin}/api/share/${w.slug}`); toast.success("Share link copied, previews will show this experience"); }} className="p-2 hover:bg-[#E5E7EB]" title="Copy share link (with social preview)" data-testid={`share-${w.id}`}>
                    <Share2 className="w-4 h-4 text-[#4B5563]" />
                  </button>
                )}
                <button onClick={(e) => { e.preventDefault(); navigate(`/admin/webinars/${w.id}/registrants`); }} className="p-2 hover:bg-[#E5E7EB]" title="Registrants" data-testid={`registrants-${w.id}`}>
                  <Users className="w-4 h-4 text-[#4B5563]" />
                </button>
                <span className="p-2 hover:bg-[#E5E7EB]"><Pencil className="w-4 h-4 text-[#4B5563]" /></span>
                <button onClick={(e) => remove(w.id, e)} className="p-2 hover:bg-red-50" title="Delete" data-testid={`delete-${w.id}`}>
                  <Trash2 className="w-4 h-4 text-[#FF3B30]" />
                </button>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
